import { useCallback, useEffect, useMemo, useReducer } from 'react';

import type { PartialDeep } from 'type-fest';

import { googleTagManagerTracking } from 'src/app/shared/utils/googleTagManagerTracking';

import type { StepConfig, StepState } from '../../types';
import { useInitialState } from '../useInitialState';
import { useStepStorage } from '../useStepStorage';

import { stepReducer } from './reducer';

type PartialPetition = PartialDeep<StepState['petition']>;
type PartialMetadata = PartialDeep<StepState['metadata']>;

type UseStepStateData = {
	petition: StepState['petition'];
	metadata: StepState['metadata'];
	currentStep: StepConfig;
	nextStep: StepConfig | undefined;
	previousStep: StepConfig | undefined;
	totalNumberOfSteps: number;
};
type UseStepStateActions = {
	gotoNextStep: (petition?: PartialPetition, options?: { skipGtmTracking?: boolean }) => void;
	gotoPreviousStep: () => void;
	skipStep: () => void;
	updatePetition: (petition: PartialPetition) => void;
	updateMetadata: (metadata: PartialMetadata) => void;
	resetStepState: () => void;
};
export type UseStepStateReturn = { data: UseStepStateData; actions: UseStepStateActions };

// eslint-disable-next-line max-lines-per-function
export function useStepState(): UseStepStateReturn {
	const [stepStorage, setStepStorage] = useStepStorage();
	const initialState = useInitialState(stepStorage);
	const [state, dispatch] = useReducer(stepReducer, initialState);
	const { currentStepIndex, steps } = state.metadata;

	const previousStep = useMemo(() => steps[currentStepIndex - 1], [currentStepIndex, steps]);
	const currentStep = useMemo(() => steps[currentStepIndex], [currentStepIndex, steps]);
	const nextStep = useMemo(() => steps[currentStepIndex + 1], [currentStepIndex, steps]);
	const totalNumberOfSteps = useMemo(() => steps.length, [steps.length]);

	// step navigation
	const gotoNextStep = (petition?: PartialPetition) => {
		// return early if last step
		if (currentStepIndex === totalNumberOfSteps - 1) return;

		// track the step navigation with the GTM tracking
		googleTagManagerTracking.push({ event: 'sap_flow', step: currentStep.name });

		dispatch({ type: 'NEXT', payload: petition });
	};

	const gotoPreviousStep = () => {
		// return early if first step
		if (currentStepIndex === 0) return;

		dispatch({ type: 'BACK' });
	};

	const skipStep = () => {
		dispatch({ type: 'SKIP' });
	};

	// manual state management
	const updatePetition = (petition: PartialPetition) => {
		dispatch({ type: 'UPDATE_PETITION', payload: petition });
	};

	const updateMetadata = (metadata: PartialMetadata) => {
		dispatch({ type: 'UPDATE_METADATA', payload: metadata });
	};

	const resetStepState = useCallback(() => {
		// only clearing step storage to prevent the UI from reverting
		// to the start of the flow after submitting the petition
		setStepStorage(null);
	}, [setStepStorage]);

	// update local storage any time we make a change to step state
	useEffect(() => {
		// remove `isEnabled` function from step config before saving to storage
		const serializedSteps = steps.map((step) => {
			return { name: step.name, isAuthenticationRequired: step.isAuthenticationRequired };
		});

		setStepStorage({ ...state, metadata: { ...state.metadata, steps: serializedSteps } });
	}, [state, setStepStorage, steps]);

	return {
		data: {
			petition: state.petition,
			metadata: state.metadata,
			currentStep,
			nextStep,
			previousStep,
			totalNumberOfSteps,
		},
		actions: {
			gotoNextStep,
			gotoPreviousStep,
			skipStep,
			updatePetition,
			updateMetadata,
			resetStepState,
		},
	};
}
